import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {ellipsis as ellipsisMixin, textSelection} from '../../styles/mixins';
import {resolveTypographyColor, TypographyColor, TypographyVariant} from './variants';

/*
 * Mixin.
 */

interface TypographyMixinProps {
  /**
   * The text variant. If the variant you need is missing, check with a designer or the design system working group.
   */
  variant?: `${TypographyVariant}`;

  /**
   * The text color. You can either use text semantic colors or accent colors.
   * @example
   * "primary"
   * @example
   * "accent.red.text-primary"
   */
  color?: TypographyColor;

  /**
   * If true, the text will not wrap and will be cut off with an ellipsis.
   * @note This can only happen with block or inline-block elements. (The component need to have a width to overflow)
   */
  shouldTruncate?: boolean;

  /**
   * If true, the text will be selectable.
   * @note By default, text-selection is disabled across the whole application.
   */
  allowSelection?: boolean;
}

export const resetTypographyMixin = css`
  color: unset;
  font-size: unset;
  font-weight: unset;
  line-height: unset;
`;

function validateTypographyVariant(theme: any, variant: string): void {
  if (!theme.typography[variant]) {
    const validVariants = Object.values(TypographyVariant).join(', ');
    throw new Error(
      `[Typography] Unknown variant: "${variant}". ` +
        `Valid variants are: ${validVariants}. ` +
        `Check libs/shared/ui/src/components/typography/variants.tsx for details.`,
    );
  }
}

export const typographyMixin = ({
  variant,
  color,
  shouldTruncate,
  allowSelection,
}: TypographyMixinProps) => css`
  ${variant &&
  css`
    font-size: ${(p) => {
      validateTypographyVariant(p.theme, variant);
      return p.theme.typography[variant].fontSize;
    }};
    font-weight: ${(p) => p.theme.typography[variant].fontWeight};
    line-height: ${(p) => p.theme.typography[variant].lineHeight};
  `}

  ${shouldTruncate && ellipsisMixin()};

  ${color &&
  css`
    color: ${resolveTypographyColor(color)};
  `}

  ${allowSelection && textSelection()};
`;

const StyledTypography = styled.span<{
  $variant?: `${TypographyVariant}`;
  $color?: TypographyColor;
  $shouldTruncate?: boolean;
  $allowSelection?: boolean;
}>`
  /* Reset margin for title variants */
  margin: 0;

  ${(props) =>
    typographyMixin({
      variant: props.$variant,
      color: props.$color,
      shouldTruncate: props.$shouldTruncate,
      allowSelection: props.$allowSelection,
    })}
`;

/*
 * Props.
 */

type ElementType = 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
export interface TypographyProps extends TypographyMixinProps {
  /**
   * The HTML element to render.
   */
  as?: ElementType;
  children: ReactNode;
}

export const Typography: FC<TypographyProps> = ({as = 'span', children, ...props}: TypographyProps) => (
  <StyledTypography
    as={as}
    $variant={props.variant}
    $color={props.color}
    $shouldTruncate={props.shouldTruncate}
    $allowSelection={props.allowSelection}
  >
    {children}
  </StyledTypography>
);
